/**
 * Team name override mapping for consistent color assignment
 * Maps shortened team names to their full names for color lookup
 *
 * This system ensures that both "white sox" and "chicago white sox"
 * will get the same colors by normalizing to the full team name.
 *
 * For teams that exist in multiple sports (e.g., Giants, Cardinals, Bears),
 * we use prefixes to disambiguate: "mlb giants", "nfl giants", etc.
 */
export const TEAM_NAME_OVERRIDES: Record<string, string> = {
  // MLB Teams
  "white sox": "chicago white sox",
  "red sox": "boston red sox",
  yankees: "new york yankees",
  dodgers: "los angeles dodgers",
  "mlb giants": "san francisco giants", // Disambiguate from NFL Giants
  cubs: "chicago cubs",
  "mlb cardinals": "st. louis cardinals", // Disambiguate from NFL Cardinals
  braves: "atlanta braves",
  phillies: "philadelphia phillies",
  mets: "new york mets",
  marlins: "miami marlins",
  nationals: "washington nationals",
  brewers: "milwaukee brewers",
  reds: "cincinnati reds",
  pirates: "pittsburgh pirates",
  padres: "san diego padres",
  diamondbacks: "arizona diamondbacks",
  rockies: "colorado rockies",
  angels: "los angeles angels",
  athletics: "oakland athletics",
  rangers: "texas rangers",
  mariners: "seattle mariners",
  astros: "houston astros",
  royals: "kansas city royals",
  "mlb tigers": "detroit tigers", // Disambiguate from CFB Tigers
  twins: "minnesota twins",
  guardians: "cleveland guardians",
  rays: "tampa bay rays",
  "blue jays": "toronto blue jays",
  orioles: "baltimore orioles",

  "usc trojans": "USC trojans",
  "ucla bruins": "UCLA bruins",

  // NFL Teams
  bills: "buffalo bills",
  dolphins: "miami dolphins",
  patriots: "new england patriots",
  jets: "new york jets",
  ravens: "baltimore ravens",
  bengals: "cincinnati bengals",
  browns: "cleveland browns",
  steelers: "pittsburgh steelers",
  texans: "houston texans",
  "nfl colts": "indianapolis colts", // Disambiguate from CFB
  colts: "indianapolis colts", // Keep for backward compatibility
  jaguars: "jacksonville jaguars",
  titans: "tennessee titans",
  chiefs: "kansas city chiefs",
  broncos: "denver broncos",
  raiders: "las vegas raiders",
  chargers: "los angeles chargers",
  cowboys: "dallas cowboys",
  "nfl cowboys": "dallas cowboys", // Disambiguate from CFB Cowboys
  "nfl giants": "new york giants", // Disambiguate from MLB Giants
  eagles: "philadelphia eagles",
  commanders: "washington commanders",
  bears: "chicago bears", // Disambiguate from CFB Bears
  "nfl bears": "chicago bears", // Disambiguate from CFB Bears
  lions: "detroit lions",
  packers: "green bay packers",
  vikings: "minnesota vikings",
  falcons: "atlanta falcons",
  panthers: "carolina panthers",
  saints: "new orleans saints",
  buccaneers: "tampa bay buccaneers",
  "nfl cardinals": "arizona cardinals", // Disambiguate from MLB Cardinals
  rams: "los angeles rams",
  "49ers": "san francisco 49ers",
  seahawks: "seattle seahawks",

  // College Football Teams
  bulldogs: "georgia bulldogs",
  longhorns: "texas longhorns",
  texas: "texas longhorns", // Map "Texas" to "texas longhorns"
  wolverines: "michigan wolverines",
  "crimson tide": "alabama crimson tide",
  ducks: "oregon ducks",
  buckeyes: "ohio state buckeyes",
  "nitanny lions": "penn state nitanny lions",
  "fighting irish": "notre dame fighting irish",
  "cfb tigers": "clemson tigers", // Disambiguate from MLB Tigers
  clemson: "clemson tigers", // Map "Clemson" to "clemson tigers"
  seminoles: "florida state seminoles",
  huskies: "washington huskies",
  volunteers: "tennessee volunteers",
  tennessee: "tennessee volunteers",
  sooners: "oklahoma sooners",
  utes: "utah utes",
  rebels: "ole miss rebels",
  badgers: "wisconsin badgers",
  hurricanes: "miami hurricanes",
  "miami fl": "miami hurricanes",
  "tar heels": "north carolina tar heels",
  hawkeyes: "iowa hawkeyes",
  "cfb cowboys": "oklahoma state cowboys", // Disambiguate from NFL Cowboys
  wildcats: "kansas state wildcats",
  "horned frogs": "texas christian horned frogs",
  "cfb bears": "baylor bears", // Disambiguate from NFL Bears
  aggies: "texas a&m aggies",
  "texas am": "texas a&m aggies", // Map "Texas AM" to "texas a&m aggies"
  "texas tech": "texas tech red raiders", // Map "Texas Tech" to "texas tech red raiders"
  "red raiders": "texas tech red raiders", // Map "Red Raiders" to "texas tech red raiders"

  // Arizona teams - disambiguate by sport
  "arizona wildcats": "arizona wildcats", // CFB Wildcats
  "arizona state": "arizona state sun devils", // CFB Sun Devils
  "sun devils": "arizona state sun devils", // CFB Sun Devils
  "arizona cardinals": "arizona cardinals", // NFL Cardinals
  "arizona diamondbacks": "arizona diamondbacks", // MLB Diamondbacks

  // Carolina teams - disambiguate by sport
  "carolina panthers": "carolina panthers", // NFL Panthers
  "north carolina": "north carolina tar heels", // CFB Tar Heels
  "south carolina": "south carolina gamecocks", // CFB Gamecocks
  gamecocks: "south carolina gamecocks", // Short name for South Carolina
  "carolina hurricanes": "carolina hurricanes", // NHL Hurricanes

  // League-specific overrides to prevent cross-league mixing
  "nfl chiefs": "kansas city chiefs", // Prevent NFL Chiefs from appearing in CFB
  TCU: "TCU horned frogs", // Fix TCU mapping
  tcu: "TCU horned frogs", // Map tcu to TCU horned frogs

  // Additional CFB team name overrides
  miami: "miami hurricanes", // Map Miami to Miami Hurricanes for CFB
  pittsburgh: "pittsburgh panthers", // Map Pittsburgh to Pittsburgh Panthers for CFB
  "nc state": "nc state wolfpack", // Map NC State to NC State Wolfpack
  minnesota: "minnesota golden gophers", // Map Minnesota to Minnesota Golden Gophers for CFB
  "ohio state": "ohio state buckeyes", // Map Ohio State to Ohio State Buckeyes
  lsu: "LSU tigers", // Map LSU to LSU Tigers
  LSU: "LSU tigers", // Map LSU to LSU Tigers
  washington: "washington huskies", // Map Washington to Washington Huskies for CFB
  auburn: "auburn tigers", // Map Auburn to Auburn Tigers
  houston: "houston cougars", // Map Houston to Houston Cougars for CFB
  cincinnati: "cincinnati bearcats", // Map Cincinnati to Cincinnati Bearcats for CFB
  ucf: "UCF knights", // Map UCF to UCF Knights
  UCF: "UCF knights", // Map UCF to UCF Knights
  byu: "BYU cougars", // Map BYU to BYU Cougars
  BYU: "BYU cougars", // Map BYU to BYU Cougars
  smu: "SMU mustangs", // Map SMU to SMU Mustangs
  SMU: "SMU mustangs", // Map SMU to SMU Mustangs
}
